import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AimOutlined, AuditOutlined, CalendarOutlined, DownloadOutlined, InboxOutlined, ReloadOutlined, RiseOutlined, SettingOutlined, ShoppingOutlined, SyncOutlined, UploadOutlined, UserOutlined } from '@ant-design/icons';
import { Alert, Button, Card, DatePicker, Segmented, Select, Spin, Switch, Tag, message } from 'antd';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import profitReportService from '../../../services/profitReportService';
import ProfitCharts from './ProfitCharts';
import ProfitabilityTable from './ProfitabilityTable';
import AdsCostSyncModal from './AdsCostSyncModal';
import { financialAdjustmentPermissions } from '../../../utils/permissions';
import './styles.css';

const { RangePicker } = DatePicker;
const KPI_METRICS = [
  { key: 'totalRevenue', label: 'Total Revenue' },
  { key: 'earning', label: 'Earning' },
  { key: 'productCost', label: 'Base Cost', inverse: true },
  { key: 'amazonAdsCost', label: 'Amazon Ads Cost', inverse: true },
  { key: 'dspAdsCost', label: 'DSP Ads Cost', inverse: true },
  { key: 'fbaStorageFee', label: 'FBA Storage Fee', inverse: true },
  { key: 'inboundFee', label: 'Inbound Fee', inverse: true },
  { key: 'refund', label: 'Refund', inverse: true },
  { key: 'netMargin', label: 'Net Margin' },
  { key: 'netMarginPercent', label: '% Net Margin', percent: true },
];

const LOAD_LABELS = {
  options: 'Đang tải danh sách Account và Niche',
  account: 'Đang tính chỉ số tổng quan và Account',
  niche: 'Đang tính báo cáo Niche',
  productLine: 'Đang tính báo cáo Nhóm sản phẩm',
  comparisonAccount: 'Đang tải cùng kỳ cho chỉ số tổng quan và Account',
  comparisonNiche: 'Đang tải cùng kỳ cho báo cáo Niche',
  comparisonProductLine: 'Đang tải cùng kỳ cho báo cáo Nhóm sản phẩm',
};

const COMPARISON_METRICS = [
  'totalRevenue', 'earning', 'productCost', 'amazonAdsCost', 'dspAdsCost',
  'fbaStorageFee', 'inboundFee', 'refund', 'sellingFee', 'fbaFee',
  'sellingAndFbaFee', 'otherCost',
  'fbaFeeAdjustment', 'netMargin', 'netMarginPercent', 'refundFbaPercent',
  'refundFbmPercent',
];

const rangeForDays = days => [dayjs().subtract(days - 1, 'day').startOf('day'), dayjs().startOf('day')];
const money = value => Number(value || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const csvMoney = value => Number(value || 0).toLocaleString('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const csvPercent = value => `${Number(value || 0).toFixed(2)}%`;
const escapeCsvCell = value => {
  let text = value === null || value === undefined ? '' : String(value);
  if (/^[=+\-@]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
};

const buildNicheOptions = niches => {
  const nichesById = new Map((niches || []).map(niche => [String(niche.id), niche]));
  const pathCache = new Map();

  const resolvePath = (niche, ancestors = new Set()) => {
    const id = String(niche?.id || '');
    if (pathCache.has(id)) return pathCache.get(id);

    const name = niche?.name || id;
    const parentId = niche?.parentNicheId ? String(niche.parentNicheId) : null;
    const parent = parentId ? nichesById.get(parentId) : null;
    const hasCycle = parentId && ancestors.has(parentId);
    const segments = parent && !hasCycle
      ? [...resolvePath(parent, new Set([...ancestors, id])), name]
      : [name];

    pathCache.set(id, segments);
    return segments;
  };

  return (niches || []).map(niche => {
    const pathSegments = resolvePath(niche);
    const path = pathSegments.join(' › ');
    return {
      value: niche.id,
      label: path,
      name: niche.name,
      path,
      pathSegments,
      parentPath: pathSegments.slice(0, -1).join(' › '),
      depth: Math.max(0, pathSegments.length - 1),
      searchText: `${niche.name || ''} ${path}`.toLocaleLowerCase('vi'),
    };
  }).sort((left, right) => left.path.localeCompare(right.path, 'vi', { sensitivity: 'base' }));
};

const deriveTotalDeltas = (total, rows = []) => {
  if (!total || !rows.length) return {};
  const previousTotals = {};

  KPI_METRICS.filter(metric => !metric.percent).forEach(metric => {
    let complete = true;
    let previousTotal = 0;
    for (const row of rows) {
      const currentValue = Number(row[metric.key] || 0);
      const delta = row.delta?.[metric.key];
      if (delta === null || delta === undefined || Math.abs(100 + Number(delta)) < 0.0001) {
        if (currentValue !== 0) complete = false;
        continue;
      }
      previousTotal += currentValue / (1 + Number(delta) / 100);
    }
    previousTotals[metric.key] = complete ? previousTotal : null;
  });

  const result = {};
  KPI_METRICS.forEach(metric => {
    let previousValue = previousTotals[metric.key];
    if (metric.key === 'netMarginPercent') {
      const previousRevenue = previousTotals.totalRevenue;
      const previousNetMargin = previousTotals.netMargin;
      previousValue = previousRevenue && previousNetMargin !== null
        ? previousNetMargin / previousRevenue * 100
        : null;
    }
    result[metric.key] = previousValue === null || previousValue === undefined || previousValue === 0
      ? null
      : Number(((Number(total[metric.key] || 0) - previousValue) / Math.abs(previousValue) * 100).toFixed(2));
  });
  return result;
};

const calculateClientDelta = (current, previous) => COMPARISON_METRICS.reduce((result, metric) => {
  const currentValue = Number(current?.[metric] || 0);
  const previousValue = Number(previous?.[metric] || 0);
  result[metric] = previousValue === 0
    ? null
    : Number(((currentValue - previousValue) / Math.abs(previousValue) * 100).toFixed(2));
  return result;
}, {});

const mergeComparisonReport = (currentReport, previousReport) => {
  if (!currentReport || !previousReport) return currentReport;
  const previousRows = new Map((previousReport.breakdown?.rows || []).map(row => [row.key, row]));
  const currentRows = (currentReport.breakdown?.rows || []).map(row => ({
    ...row,
    delta: calculateClientDelta(row, previousRows.get(row.key)),
  }));
  const currentTotal = currentReport.breakdown?.total;

  return {
    ...currentReport,
    breakdown: {
      ...currentReport.breakdown,
      rows: currentRows,
      total: currentTotal ? {
        ...currentTotal,
        delta: calculateClientDelta(currentTotal, previousReport.breakdown?.total),
      } : currentTotal,
    },
    ...(currentReport.charts ? {
      charts: {
        ...currentReport.charts,
        previous: previousReport.charts?.current || [],
      },
    } : {}),
  };
};

const FulfillmentRatio = ({ data }) => {
  const fbaOrders = Number(data?.fbaOrdersCount || 0);
  const fbmOrders = Number(data?.fbmOrdersCount || 0);
  const totalOrders = fbaOrders + fbmOrders;
  const fbaRatio = totalOrders ? fbaOrders / totalOrders * 100 : 0;
  const fbmRatio = totalOrders ? 100 - fbaRatio : 0;

  return (
    <section className="profit-fulfillment-ratio">
      <div className="profit-fulfillment-heading">
        <strong>Tỷ lệ đơn hàng FBA vs FBM (Fulfillment Ratio)</strong>
        <small>Tỷ lệ phần trăm đơn hàng hoàn tất qua kho FBA so với tự vận chuyển (FBM)</small>
      </div>

      <div className="profit-fulfillment-bar" aria-label={`FBA ${fbaRatio.toFixed(1)}%, FBM ${fbmRatio.toFixed(1)}%`}>
        <div className="is-fba" style={{ width: `${fbaRatio}%` }}>
          {fbaRatio >= 12 && `${fbaRatio.toFixed(1)}% FBA`}
        </div>
        <div className="is-fbm" style={{ width: `${fbmRatio}%` }}>
          {fbmRatio >= 12 && `${fbmRatio.toFixed(1)}% FBM`}
        </div>
      </div>

      <div className="profit-fulfillment-cards">
        <div className="profit-fulfillment-card is-fba">
          <span className="profit-fulfillment-icon"><InboxOutlined /></span>
          <div>
            <strong>Fulfillment by Amazon (FBA)</strong>
            <small>Doanh thu ước tính: <b>{money(data?.fbaRevenue)}</b></small>
          </div>
          <b className="profit-fulfillment-orders">{fbaOrders.toLocaleString('vi-VN')}<small>đơn hàng</small></b>
        </div>
        <div className="profit-fulfillment-card is-fbm">
          <span className="profit-fulfillment-icon"><ShoppingOutlined /></span>
          <div>
            <strong>Fulfillment by Merchant (FBM)</strong>
            <small>Doanh thu ước tính: <b>{money(data?.fbmRevenue)}</b></small>
          </div>
          <b className="profit-fulfillment-orders">{fbmOrders.toLocaleString('vi-VN')}<small>đơn hàng</small></b>
        </div>
      </div>
    </section>
  );
};

const ProfitReport = () => {
  const navigate = useNavigate();
  const user = useSelector(state => state.auth.user);
  const canImportDsp = financialAdjustmentPermissions.canCreate(user)
    || financialAdjustmentPermissions.canManage(user);
  const optionsLoadedRef = useRef(false);
  const [sellerAccounts, setSellerAccounts] = useState([]);
  const [niches, setNiches] = useState([]);
  const [range, setRange] = useState(() => rangeForDays(30));
  const [preset, setPreset] = useState(30);
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);
  const [selectedNicheId, setSelectedNicheId] = useState();
  const [inboundMode, setInboundMode] = useState('business');
  const [showComparison, setShowComparison] = useState(true);
  const [accountReport, setAccountReport] = useState(null);
  const [nicheReport, setNicheReport] = useState(null);
  const [productLineReport, setProductLineReport] = useState(null);
  const [loadingStage, setLoadingStage] = useState(null);
  const [error, setError] = useState(null);
  const [adsSyncModalOpen, setAdsSyncModalOpen] = useState(false);
  const [adsSyncLoading, setAdsSyncLoading] = useState(false);

  const accountOptions = useMemo(() => sellerAccounts.map(account => ({
    value: account.id,
    label: account.name || account.sellerId,
  })), [sellerAccounts]);

  const nicheOptions = useMemo(() => buildNicheOptions(niches), [niches]);

  const loadReport = useCallback(async signal => {
    setError(null);
    setAccountReport(null);
    setNicheReport(null);
    setProductLineReport(null);

    try {
      if (!optionsLoadedRef.current) {
        setLoadingStage('options');
        const options = await profitReportService.getOptions(signal);
        if (signal?.aborted) return;
        setSellerAccounts(options?.sellerAccounts || []);
        setNiches(options?.niches || []);
        optionsLoadedRef.current = true;
      }

      const params = {
        startDate: range[0].format('YYYY-MM-DD'),
        endDate: range[1].format('YYYY-MM-DD'),
        sellerAccountIds: selectedAccountIds.join(','),
        nicheIds: selectedNicheId || '',
        inboundMode,
        includeComparison: 'false',
      };

      // Deliberately sequential: at most one heavy Profit Report query occupies
      // the database pool at a time.
      setLoadingStage('account');
      const account = await profitReportService.getBreakdown('account', params, signal);
      if (signal?.aborted) return;
      setAccountReport(account);

      const comparisonParams = {
        ...params,
        startDate: account.period.comparisonStartDate,
        endDate: account.period.comparisonEndDate,
      };

      // Load the comparison for each main report immediately after that main
      // report completes. This lets the KPI and table deltas appear early while
      // still keeping every heavy query sequential.
      setLoadingStage('comparisonAccount');
      const previousAccount = await profitReportService.getBreakdown('account', comparisonParams, signal);
      if (signal?.aborted) return;
      setAccountReport(current => mergeComparisonReport(current, previousAccount));

      setLoadingStage('niche');
      const niche = await profitReportService.getBreakdown('niche', params, signal);
      if (signal?.aborted) return;
      setNicheReport(niche);

      setLoadingStage('comparisonNiche');
      const previousNiche = await profitReportService.getBreakdown('niche', comparisonParams, signal);
      if (signal?.aborted) return;
      setNicheReport(current => mergeComparisonReport(current, previousNiche));

      setLoadingStage('productLine');
      const productLine = await profitReportService.getBreakdown('product-line', params, signal);
      if (signal?.aborted) return;
      setProductLineReport(productLine);

      setLoadingStage('comparisonProductLine');
      const previousProductLine = await profitReportService.getBreakdown('product-line', comparisonParams, signal);
      if (signal?.aborted) return;
      setProductLineReport(current => mergeComparisonReport(current, previousProductLine));
    } catch (requestError) {
      if (requestError.code !== 'ERR_CANCELED') {
        const detail = requestError.response?.data?.message || requestError.message;
        setError(detail);
        message.error(`Không thể tải Profit Report: ${detail}`);
      }
    } finally {
      if (!signal?.aborted) setLoadingStage(null);
    }
  }, [range, selectedAccountIds, selectedNicheId, inboundMode]);

  useEffect(() => {
    const controller = new AbortController();
    loadReport(controller.signal);
    return () => controller.abort();
  }, [loadReport]);

  const selectPreset = days => {
    setPreset(days);
    setRange(rangeForDays(days));
  };

  const coverage = accountReport?.coverage || nicheReport?.coverage || productLineReport?.coverage || {};
  const accountTotal = accountReport?.breakdown?.total;
  const kpiDeltas = useMemo(() => accountTotal?.delta || deriveTotalDeltas(
    accountTotal,
    accountReport?.breakdown?.rows || []
  ), [accountReport, accountTotal]);
  const loading = Boolean(loadingStage);

  const syncAdsCosts = useCallback(async payload => {
    setAdsSyncLoading(true);
    try {
      const response = await profitReportService.syncAdsCosts(payload);
      const queuedCount = response?.data?.jobs?.length || 0;
      const skippedCount = response?.data?.skipped?.length || 0;
      message.success(`Đã tạo job đồng bộ Ads Cost cho ${queuedCount} Seller Account`);
      if (skippedCount) {
        message.warning(`${skippedCount} Seller Account bị bỏ qua vì chưa sẵn sàng đồng bộ`);
      }
      setAdsSyncModalOpen(false);
    } catch (requestError) {
      const detail = requestError.response?.data?.message || requestError.message;
      message.error(`Không thể đồng bộ Ads Cost: ${detail}`);
    } finally {
      setAdsSyncLoading(false);
    }
  }, []);

  const exportCsv = useCallback(() => {
    const rows = accountReport?.breakdown?.rows || [];
    const total = accountReport?.breakdown?.total;
    if (!rows.length || !total) {
      message.warning('Chưa có dữ liệu Account để xuất CSV');
      return;
    }

    const periodStart = dayjs(accountReport.period?.startDate || range[0]);
    const periodEnd = dayjs(accountReport.period?.endDate || range[1]);
    const periodLabel = `${periodStart.format('D/M/YYYY')} - ${periodEnd.format('D/M/YYYY')}`;
    const columns = [
      'Date', 'Account', 'Revenue', 'Earning', 'Storage', 'Inbound', 'Refund',
      'FBA Fee', 'Selling Fee', 'AMZ Ads', 'DSP', 'Product Cost', 'Other Cost',
      'Net Margin', 'Margin %',
    ];
    const availableMoney = (record, metric) => coverage[metric] === false ? '' : csvMoney(record[metric]);
    const dataRows = rows.map(row => [
      periodLabel,
      row.name,
      csvMoney(row.totalRevenue),
      csvMoney(row.earning),
      availableMoney(row, 'fbaStorageFee'),
      availableMoney(row, 'inboundFee'),
      csvMoney(row.refund),
      csvMoney(row.fbaFee),
      csvMoney(row.sellingFee),
      availableMoney(row, 'amazonAdsCost'),
      availableMoney(row, 'dspAdsCost'),
      csvMoney(row.productCost),
      csvMoney(row.otherCost),
      csvMoney(row.netMargin),
      csvPercent(row.netMarginPercent),
    ]);
    dataRows.push([
      'TOTAL',
      '',
      csvMoney(total.totalRevenue),
      csvMoney(total.earning),
      availableMoney(total, 'fbaStorageFee'),
      availableMoney(total, 'inboundFee'),
      csvMoney(total.refund),
      csvMoney(total.fbaFee),
      csvMoney(total.sellingFee),
      availableMoney(total, 'amazonAdsCost'),
      availableMoney(total, 'dspAdsCost'),
      csvMoney(total.productCost),
      csvMoney(total.otherCost),
      csvMoney(total.netMargin),
      csvPercent(total.netMarginPercent),
    ]);

    // Excel uses the OS regional list separator. Explicitly declaring `;`
    // keeps the file auto-splittable on Vietnamese locales while currency
    // values can still contain comma thousands separators.
    const csv = `sep=;\r\n${[columns, ...dataRows]
      .map(row => row.map(escapeCsvCell).join(';'))
      .join('\r\n')}`;
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `profit_report_${periodStart.format('YYYY-MM-DD')}_${periodEnd.format('YYYY-MM-DD')}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    message.success('Đã xuất Profit Report CSV');
  }, [accountReport, coverage, range]);

  return (
    <div className="profit-report-page">
      <main className="profit-report-panel">
        <div className="profit-report-heading-row">
          <div>
            <h1>Báo Cáo Lợi Nhuận &amp; Tài Chính (Profit &amp; P&amp;L Report)</h1>
            <p>Doanh thu, chi phí, hoàn tiền và lợi nhuận theo Account, Niche và Product Line.</p>
          </div>
          <Tag color={loading ? 'processing' : 'green'}>
            {loading ? LOAD_LABELS[loadingStage] || 'Đang tải dữ liệu' : 'Live data'}
          </Tag>
        </div>

        <div className="profit-report-actions">
          <Button
            icon={<SyncOutlined />}
            onClick={() => setAdsSyncModalOpen(true)}
            disabled={!sellerAccounts.length}
          >
            Đồng bộ Ads Cost
          </Button>
          {canImportDsp && (
            <Button
              icon={<UploadOutlined />}
              onClick={() => navigate('/financial-transactions?tab=dsp')}
            >
              Import DSP Cost
            </Button>
          )}
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            disabled={!accountReport?.breakdown?.rows?.length}
            onClick={exportCsv}
          >
            Xuất CSV
          </Button>
        </div>

        <AdsCostSyncModal
          open={adsSyncModalOpen}
          loading={adsSyncLoading}
          sellerAccounts={sellerAccounts}
          initialSellerAccountIds={selectedAccountIds}
          initialRange={range}
          onCancel={() => !adsSyncLoading && setAdsSyncModalOpen(false)}
          onSubmit={syncAdsCosts}
        />

        <div className="profit-report-filters">
          <div className="profit-report-filter-group profit-report-filter-period">
            <span className="profit-report-filter-label"><CalendarOutlined /> Khoảng thời gian</span>
            <div className="profit-report-period-controls">
              <div className="profit-report-periods">
                <Button size="small" disabled={loading} type={preset === 7 ? 'primary' : 'default'} onClick={() => selectPreset(7)}>7 ngày</Button>
                <Button size="small" disabled={loading} type={preset === 14 ? 'primary' : 'default'} onClick={() => selectPreset(14)}>14 ngày</Button>
                <Button size="small" disabled={loading} type={preset === 30 ? 'primary' : 'default'} onClick={() => selectPreset(30)}>30 ngày</Button>
              </div>
              <RangePicker
                value={range} allowClear={false} format="DD/MM/YYYY" disabled={loading}
                suffixIcon={<CalendarOutlined />}
                onChange={dates => {
                  if (dates?.[0] && dates?.[1]) {
                    setPreset('custom');
                    setRange(dates);
                  }
                }}
              />
            </div>
          </div>

          <div className="profit-report-filter-group profit-report-filter-account">
            <span className="profit-report-filter-label"><UserOutlined /> Account</span>
            <Select
              mode="multiple" allowClear maxTagCount="responsive" disabled={loading}
              className="profit-report-account-select" value={selectedAccountIds}
              placeholder="Tất cả Account" options={accountOptions} onChange={setSelectedAccountIds}
            />
          </div>

          <div className="profit-report-filter-group profit-report-filter-niche">
            <span className="profit-report-filter-label"><AimOutlined /> Niche</span>
            <Select
              allowClear showSearch disabled={loading}
              className="profit-report-niche-select" value={selectedNicheId}
              popupClassName="profit-report-niche-dropdown"
              listItemHeight={48} listHeight={336}
              placeholder="Tất cả Niche (ALL)" options={nicheOptions} onChange={setSelectedNicheId}
              filterOption={(input, option) => option?.searchText?.includes(input.trim().toLocaleLowerCase('vi'))}
              optionRender={option => (
                <div className="profit-report-niche-option">
                  {option.data.parentPath && (
                    <span className="profit-report-niche-option-path">{option.data.parentPath}</span>
                  )}
                  <span className="profit-report-niche-option-name">{option.data.name}</span>
                </div>
              )}
            />
          </div>

          <div className="profit-report-filter-group profit-report-filter-mode">
            <span className="profit-report-filter-label">Ghi nhận Inbound Fee</span>
            <Segmented
              value={inboundMode} className="profit-report-inbound-segmented" disabled={loading}
              onChange={setInboundMode}
              options={[
                {
                  value: 'business',
                  label: <span className="profit-report-mode-option"><RiseOutlined /> Business</span>,
                },
                {
                  value: 'accounting',
                  label: <span className="profit-report-mode-option"><AuditOutlined /> Accounting</span>,
                },
              ]}
            />
          </div>

          <div className="profit-report-filter-group profit-report-filter-comparison">
            <span className="profit-report-filter-label">So sánh cùng kỳ</span>
            <div className="profit-report-comparison-control">
              <Switch checked={showComparison} onChange={setShowComparison} disabled={loading} />
              <span>{showComparison ? 'Đang hiển thị' : 'Đang ẩn'}</span>
            </div>
          </div>

          <Button
            className="profit-report-reload" icon={<ReloadOutlined />} loading={loading}
            disabled={loading} onClick={() => loadReport()}
          >
            Tải lại
          </Button>
        </div>

        {accountTotal && <FulfillmentRatio data={accountTotal} />}

        {error && <Alert type="error" showIcon message="Không tải được dữ liệu Profit Report" description={error} />}
        <Spin spinning={loadingStage === 'options' || loadingStage === 'account'}>
          <section className="profit-report-kpi-section">
            <div className="profit-report-section-title">
              <span>Chỉ số tổng quan</span>
              <Button size="small" icon={<SettingOutlined />} disabled>Custom metrics</Button>
            </div>
            <div className="profit-report-kpi-grid">
              {KPI_METRICS.map(metric => {
                const available = coverage[metric.key] !== false;
                const delta = kpiDeltas?.[metric.key];
                const deltaPositive = metric.inverse ? delta <= 0 : delta >= 0;
                return (
                  <Card key={metric.key} size="small" className="profit-report-kpi-card">
                    <span className="profit-report-kpi-label">{metric.label}</span>
                    <strong>{available && accountTotal
                      ? metric.percent ? `${Number(accountTotal[metric.key] || 0).toFixed(2)}%` : money(accountTotal[metric.key])
                      : '—'}</strong>
                    {!available ? <small>Chưa có nguồn dữ liệu</small> : showComparison ? (
                      delta === null || delta === undefined
                        ? <small>Chưa có dữ liệu cùng kỳ</small>
                        : <small className={`profit-kpi-delta ${deltaPositive ? 'is-positive' : 'is-negative'}`}>
                          {delta >= 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}% so với cùng kỳ
                        </small>
                    ) : <small>Theo khoảng ngày đã chọn</small>}
                  </Card>
                );
              })}
            </div>
          </section>

          <ProfitCharts
            charts={accountReport?.charts}
            reportEndDate={accountReport?.period?.endDate}
            accountRows={accountReport?.breakdown?.rows}
            coverage={coverage}
            showComparison={showComparison}
          />

          <ProfitabilityTable
            title="BẢNG PHÂN BỔ LỢI NHUẬN TÀI CHÍNH THEO ACCOUNT (P&L ACCOUNT BREAKDOWN)"
            nameLabel="Account" data={accountReport?.breakdown} coverage={coverage}
            showComparison={showComparison}
          />
        </Spin>

        <ProfitabilityTable
          title="BÁO CÁO MỨC ĐỘ SINH LỜI THEO NICHE (NICHE PROFITABILITY)"
          nameLabel="Niche Name" data={nicheReport?.breakdown} coverage={coverage}
          showComparison={showComparison} showRating
        />
        <ProfitabilityTable
          title="BÁO CÁO MỨC ĐỘ SINH LỜI THEO NHÓM SẢN PHẨM (PRODUCT LINE PROFITABILITY)"
          nameLabel="Product Line" data={productLineReport?.breakdown} coverage={coverage}
          showComparison={showComparison} showRating
        />
      </main>
    </div>
  );
};

export default ProfitReport;
